import React from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { useCart } from "../../Services/CartContext";
import { toast } from "react-toastify";
import { BASE_URL } from "../../Assets/Constants";
import axios from "axios";

function CartItems() {
  const themes = useTheme();
  const isSmallScreen = useMediaQuery(themes.breakpoints.down("md"));
  const navigate = useNavigate();
  const { cart, setCart } = useCart();

  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const token = loginData?.token
  const userId = loginData?.user?._id || "guest";

  const userCartItems = (cart[userId] || []).filter((item) => item.showInCart !== false);

  const calculateTotal = (items) => {
    return items.reduce((total, item) => {
      const priceCategory = getPriceCategory(item);
      if (!priceCategory) return total;

      let itemTotal = 0;
      Object.entries(item.ageCounts || {}).forEach(([ageName, count]) => {
        const priceInfo = priceCategory.prices.find(price => price.age?.name === ageName);
        if (priceInfo) {
          const priceValue = parseFloat(priceInfo.convertsalePrice) || parseFloat(priceInfo.convertedPrice) || 0;
          itemTotal += count * priceValue;
        }
      });

      return total + itemTotal;
    }, 0).toFixed(2);
  };

  const getPriceCategory = (item) => {
    const timeSlot = item.timeSlot;

    let priceCategory = item.experienceData.priceCategories.find(
      category => category.timeSlot === timeSlot
    );

    if (!priceCategory && (timeSlot === null || timeSlot === undefined)) {
      priceCategory = item.experienceData.priceCategories.find(
        category => category.timeSlot === "" || category.timeSlot === " "
      );
    }

    if (!priceCategory && item.experienceData.priceCategories.length > 0) {
      priceCategory = item.experienceData.priceCategories[0];
    }

    return priceCategory;
  };

  const handleDelete = (id) => {
    setCart((prev) => {
      const updatedCart = { ...prev };

      const loginData = JSON.parse(localStorage.getItem("loginData"));
      const currentUserId = loginData?.user?._id || "guest";

      if (updatedCart[currentUserId]) {
        updatedCart[currentUserId] = updatedCart[currentUserId].filter(
          (item) => item.experienceData._id !== id
        );

        if (updatedCart[currentUserId].length === 0) {
          delete updatedCart[currentUserId];
        }
      }

      localStorage.setItem("cart", JSON.stringify(updatedCart));

      return updatedCart;
    });

    toast.success("Item removed from cart!", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
      style: { backgroundColor: "red", color: "white" },
    });
  };

  const handleIncrement = (id, ageType) => {
    setCart((prev) => {
      const userCart = prev[userId] || [];

      const updatedCart = userCart.map((item) => {
        if (item.experienceData._id === id) {
          const priceCategory = item.experienceData.priceCategories.find(
            (category) => category.timeSlot === item.timeSlot
          );

          const newAgeCounts = { ...(item.ageCounts || {}) };
          newAgeCounts[ageType] = (newAgeCounts[ageType] || 0) + 1;

          let updatedTotal = 0;

          Object.entries(newAgeCounts).forEach(([age, count]) => {
            const priceInfo = priceCategory?.prices.find(
              p => p.age?.name === age
            );

            if (priceInfo) {
              updatedTotal += count * (priceInfo.convertsalePrice || priceInfo.convertedPrice || 0);
            }
          });

          return {
            ...item,
            ageCounts: newAgeCounts,
            total: updatedTotal.toFixed(2)
          };
        }
        return item;
      });

      return {
        ...prev,
        [userId]: updatedCart,
      };
    });
  };

  const handleDecrement = (id, ageType) => {
    setCart((prev) => {
      const userCart = prev[userId] || [];

      const updatedCart = userCart.map((item) => {
        if (item.experienceData._id === id) {
          const priceCategory = item.experienceData.priceCategories.find(
            (category) => category.timeSlot === item.timeSlot
          );

          const newAgeCounts = { ...(item.ageCounts || {}) };

          if (newAgeCounts[ageType] && newAgeCounts[ageType] > 1) {
            newAgeCounts[ageType] -= 1;
          }

          let updatedTotal = 0;

          Object.entries(newAgeCounts).forEach(([age, count]) => {
            const priceInfo = priceCategory?.prices.find(
              p => p.age?.name === age
            );

            if (priceInfo) {
              updatedTotal += count * (priceInfo.convertsalePrice || priceInfo.convertedPrice || 0);
            }
          });

          return {
            ...item,
            ageCounts: newAgeCounts,
            total: updatedTotal.toFixed(2)
          };
        }
        return item;
      });

      return {
        ...prev,
        [userId]: updatedCart,
      };
    });
  };

  const handleCheckout = async (item) => {
    const formattedDate = item.date.replace(/\//g, "-");
    const headers = {};

    const priceCategory = getPriceCategory(item);

    if (!priceCategory) {
      toast.error("Could not find price information for this experience", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    let ageCategories = [];

    Object.entries(item.ageCounts || {}).forEach(([ageName, count]) => {
      const priceInfo = priceCategory.prices.find(price => price.age?.name === ageName);

      if (priceInfo && count > 0) {
        ageCategories.push({
          name: priceInfo.age?._id || "",
          count: count,
          id: priceInfo.id
        });
      }
    });

    if (ageCategories.length === 0 && priceCategory.prices && priceCategory.prices.length > 0) {
      const firstPrice = priceCategory.prices[0];
      if (firstPrice && firstPrice.age) {
        ageCategories.push({
          name: firstPrice.age._id || "",
          count: 1,
          id: firstPrice.id
        });
      }
    }

    if (ageCategories.length === 0) {
      toast.error("Please select at least one ticket", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    const timeSlotToSend = item.timeSlot === null ? " " : (item.timeSlot || "");

    let calculatedTotal = 0;
    Object.entries(item.ageCounts || {}).forEach(([ageName, count]) => {
      const priceInfo = priceCategory.prices.find(price => price.age?.name === ageName);
      if (priceInfo) {
        calculatedTotal += count * (parseFloat(priceInfo.convertsalePrice) || parseFloat(priceInfo.convertedPrice) || 0);
      }
    });

    const payload = {
      experienceId: item.experienceData?._id,
      ageCategories,
      timeSlot: timeSlotToSend,
      date: formattedDate,
      total: calculatedTotal.toFixed(2),
      userType: token ? "Registered" : "Guest",
      currency: item.convertedsymbol,
      showInCart: false,
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    console.log(payload, "PAYLOADDDD");
    

    try {
      const response = await axios.post(`${BASE_URL}/user/booking/step1`, payload, { headers });
      token
        ? navigate("/checkout", {
          state: {
            bookingData: response.data,
            name: item?.experienceData?.name,
            location: item?.experienceData?.location,
          },
        })
        : navigate("/fillForm", {
          state: {
            bookingData: response.data,
            name: item?.experienceData?.name,
            location: item?.experienceData?.location,
          },
        });

      toast.success(response?.data?.message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        style: { backgroundColor: "orange", color: "white" },
      });

    } catch (error) {
      console.log(error?.response, "ERRORS ");

      if (error?.response?.data?.error) {
        const errorMsg = Array.isArray(error.response.data.error)
          ? error.response.data.error.map(err => err.msg).join(", ")
          : "Failed to proceed with checkout.";

        toast.error(errorMsg, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      } else {
        toast.error("Failed to proceed with checkout.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      }
    }
  }

  const getAvailableAgeCategories = (item) => {
    const timeSlot = item.timeSlot;

    let priceCategory = item.experienceData.priceCategories.find(
      category => category.timeSlot === timeSlot
    );

    if (!priceCategory && (timeSlot === null || timeSlot === undefined)) {
      priceCategory = item.experienceData.priceCategories.find(
        category => category.timeSlot === "" || category.timeSlot === " "
      );
    }

    if (!priceCategory && item.experienceData.priceCategories.length > 0) {
      priceCategory = item.experienceData.priceCategories[0];
    }

    if (!priceCategory) return [];

    return priceCategory.prices.map(price => price.age?.name).filter(Boolean);
  };

  return (
    <Box sx={{ p: 3 }}>
      {userCartItems.length > 0 ? (
        userCartItems.map((item) => (
          <Box
            key={item.experienceData._id}
            sx={{
              borderTop: "1px solid #ECECEC",
              borderBottom: "1px solid #ECECEC",
              borderRadius: "8px",
              overflow: "hidden",
              mb: 4,
              mx: "auto",
              display: "flex", 
              justifyContent: "center",
              alignItems: "center",
              width: { xs: "100%", md: "80%" }
            }}
          >
            <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{ p: 2 }}>
              <Grid item xs={12} md={1.5}>
                <img
                  src={item?.experienceData?.photos[0]}
                  alt="Destination"
                  style={{
                    width: isSmallScreen ? "180px" : "80px",
                    height: isSmallScreen ? "180px" : "80px",
                    borderRadius: "8px",
                  }}
                />
              </Grid>

              <Grid item xs={12} md={1.5}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: themes.colors.text }}
                >
                  Destination
                </Typography>
                <Typography
                  sx={{
                    color: themes.colors.textLight,
                    fontSize: "0.9rem",
                    marginTop: "12px",
                  }}
                >
                  {item?.experienceData?.name}
                </Typography>
              </Grid>

              <Grid item xs={12} md={1.5}>
                <Typography
                  sx={{
                    color: themes.colors.text,
                    fontWeight: 600,
                    mb: 1,
                    fontSize: "0.9rem",
                  }}
                >
                  Date
                </Typography>
                <Typography
                  sx={{
                    color: themes.colors.textLight,
                    fontSize: "0.9rem",
                    fontWeight: 400,
                    textDecoration: "underline",
                    marginTop: "15px",
                  }}
                >
                  {item.date}
                </Typography>
              </Grid>

              {/* Dynamic Age Categories */}
              {getAvailableAgeCategories(item).map((ageType) => {
                const count = (item.ageCounts && item.ageCounts[ageType]) || 0;

                return (
                  <Grid item xs={12} md={1.5} key={ageType}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      {ageType}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                      <IconButton
                        sx={{ px: 0 }}
                        onClick={() => handleDecrement(item.experienceData._id, ageType)}
                      >
                        <RemoveIcon sx={{ color: themes.colors.orange }} />
                      </IconButton>
                      <Typography sx={{ mx: 1, color: themes.colors.textLight }}>
                        {count}
                      </Typography>
                      <IconButton
                        sx={{ px: 0 }}
                        onClick={() => handleIncrement(item.experienceData._id, ageType)}
                      >
                        <AddIcon sx={{ color: themes.colors.orange }} />
                      </IconButton>
                    </Box>
                  </Grid>
                );
              })}

              <Grid item xs={12} md={2} sx={{ textAlign: "center", marginTop: "20px" }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: themes.colors.orange,
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    mb: 1,
                    ":hover": { backgroundColor: "darkorange" },
                  }}
                  onClick={() => handleCheckout(item)}
                >
                  Checkout
                </Button>

                <IconButton onClick={() => handleDelete(item.experienceData._id)}>
                  <DeleteIcon sx={{ color: themes.colors.orange }} />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: themes.colors.textLight,
              fontWeight: 500,
              mb: 3,
              fontSize: "1rem",
            }}
          >
            No items in your cart. Start adding experiences!
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: themes.colors.orange,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "16px",
              px: 4,
              py: 1.5,
              borderRadius: "8px",
              width: "30%",
              ":hover": { backgroundColor: "darkorange" },
            }}
            onClick={() => navigate("/experience")}
          >
            Continue Exploring
          </Button>
        </Box>
      )}
      <Box sx={{
        width: { xs: "100%", md: "80%" },
        mx: "auto",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
        mt: 4,
      }}>
        {userCartItems.length > 0 && (
          <Grid container alignItems="center" justifyContent="space-evenly">
            <Grid
              item
              sx={{
                backgroundColor: themes.colors.orange,
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                width: "50%",
                borderRadius: "10px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: 'pointer',
                py: 1,
                ":hover": { backgroundColor: "darkorange" },
              }}
              onClick={() => navigate("/experience")}
            >
              <Button
                sx={{
                  backgroundColor: "transparent",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "14px",
                  ":hover": { backgroundColor: "darkorange" },
                }}
              >
                Continue Exploring
              </Button>
            </Grid>

            <Grid item>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: themes.colors.text,
                }}
              >
                Total: {userCartItems.length > 0 ? userCartItems[0]?.convertedsymbol : ''}
                {calculateTotal(userCartItems)}
              </Typography>
            </Grid>

          </Grid>
        )}
      </Box>
    </Box>
  );
}

export default CartItems;